import React, { useState } from 'react';
import { LogoSVG, WhatsAppIcon } from '../../components/Icons';
import { WA_NUMBER } from '../../data/constants';
import { usePageMeta } from '../../utils/usePageMeta';
import { promesaDeRespuesta, waLink } from '../../utils/whatsapp';
import WhatsAppButton from '../../components/WhatsApp/WhatsAppButton';
import { GoogleRatingBadge } from '../../components/Resenas/Resenas';
import HeroVariante, { useVarianteAnuncio } from '../../components/HeroVariante/HeroVariante';
import '../DerechoLaboral/DerechoLaboral.css';

const WA_MSG = 'Hola, vi su página de Derecho Civil y tengo una consulta. ¿Pueden ayudarme?';

interface FAQItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}

interface FAQGroup {
  label: string;
  items: FAQItem[];
}

const groups: FAQGroup[] = [
  {
    label: 'I. Arrendamiento',
    items: [
      {
        id: 'arrendatario-no-desaloja',
        question: '¿Qué puedo hacer si mi arrendatario no quiere desalojar la propiedad?',
        answer: (
          <div className="faq-body">
            <p>Si el arrendatario no entrega la propiedad tras el término del contrato o por falta de pago, se debe iniciar un <strong>juicio de terminación de contrato de arrendamiento</strong> por no pago de rentas o de restitución por expiración del plazo.</p>
            <p>Gracias a la <strong>"Ley Devuélveme mi Casa"</strong>, hoy existen procedimientos más rápidos que permiten solicitar la restitución anticipada del inmueble con auxilio de la fuerza pública en plazos más acotados, si se cumplen ciertos requisitos.</p>
            <p className="faq-note">📌 Idealmente, el contrato debe constar por escrito. Si el contrato es verbal, el juicio se vuelve mucho más lento y complejo de probar.</p>
          </div>
        ),
      },
    ],
  },
  {
    label: 'II. Nombre y Apellidos',
    items: [
      {
        id: 'cambio-nombre',
        question: '¿Cómo puedo cambiar mi nombre o el orden de mis apellidos?',
        answer: (
          <div className="faq-body">
            <p>Existen dos vías dependiendo de lo que se busque:</p>
            <ul>
              <li>
                <strong>Cambio de orden de apellidos:</strong> Se puede realizar mediante un trámite administrativo directamente en el Registro Civil (una sola vez en la vida).
              </li>
              <li>
                <strong>Cambio de nombre o apellidos por otras causas:</strong> Si deseas cambiar tu nombre porque es ridículo, te causa menoscabo, o eres conocido por más de 5 años con otro nombre, se debe realizar un procedimiento judicial ante un Juzgado Civil.
              </li>
            </ul>
          </div>
        ),
      },
    ],
  },
  {
    label: 'III. Interdicción y Discapacidad',
    items: [
      {
        id: 'interdiccion',
        question: '¿Cómo se tramita la interdicción por incapacidad?',
        answer: (
          <div className="faq-body">
            <p>La interdicción busca declarar que una persona <strong>no es capaz de administrarse por sí misma</strong> (por demencia o disipación) y nombrarle un curador. Se tramita ante los Juzgados Civiles y requiere:</p>
            <ul>
              <li>Informes médicos y peritajes que acrediten la condición</li>
              <li>Audiencia con parientes cercanos</li>
              <li>Inspección del juez</li>
            </ul>
          </div>
        ),
      },
    ],
  },
  {
    label: 'IV. Otras Consultas Civiles',
    items: [
      {
        id: 'estudio-titulos',
        question: '¿Qué es el estudio de títulos y por qué es necesario antes de comprar una propiedad?',
        answer: (
          <div className="faq-body">
            <p>Es el <strong>análisis jurídico</strong> que realiza un abogado sobre la historia de una propiedad (generalmente los últimos 10 años) para asegurar que quien vende es el dueño real y que la propiedad no tiene embargos, hipotecas o prohibiciones que impidan la venta.</p>
          </div>
        ),
      },
      {
        id: 'posesion-efectiva',
        question: '¿Qué puedo hacer si una persona falleció y no dejó testamento?',
        answer: (
          <div className="faq-body">
            <p>Se debe tramitar la <strong>Posesión Efectiva Intestada</strong>. Este trámite se realiza ante el Registro Civil y permite que los herederos legales (cónyuge, hijos, etc.) puedan disponer de los bienes del fallecido.</p>
            <p>Dicho trámite puede ser realizado indistintamente por cualquiera de los herederos. Una vez obtenida la posesión efectiva, se debe obtener el <strong>certificado de impuesto a la herencia</strong> ante el Servicio de Impuestos Internos.</p>
          </div>
        ),
      },
      {
        id: 'cobro-deuda',
        question: '¿Cómo puedo cobrar una deuda si tengo un pagaré o cheque protestado?',
        answer: (
          <div className="faq-body">
            <p>Se debe iniciar un <strong>Juicio Ejecutivo</strong>. Este procedimiento es más rápido que un juicio ordinario y busca el embargo de bienes del deudor para asegurar el pago de lo adeudado, siempre que el documento cumpla con los requisitos legales.</p>
          </div>
        ),
      },
    ],
  },
];

const DerechoCivil: React.FC = () => {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const variante = useVarianteAnuncio('civil');

  // Con campaña, el mensaje llega diciendo a qué viene la persona.

  const waUrl = waLink(variante ? variante.mensaje : WA_MSG);


  usePageMeta(
    'Derecho Civil en Concepción | Preguntas Frecuentes | Souza & Villa Abogadas',
    'Abogadas civiles en Concepción. Resolvemos tus dudas sobre arrendamiento, herencias, cobro de deudas, contratos y más. Primera consulta gratuita.',
    '/areas/derecho-civil',
  );

  const toggle = (id: string) => {
    setOpenItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="dl-page">
      {/* Header */}
      <header className="dl-header">
        <a href="/" className="dl-logo">
          <LogoSVG size={44} />
          <div className="dl-logo-text">
            <span className="dl-logo-name">Souza &amp; Villa</span>
            <span className="dl-logo-tagline">Abogadas</span>
          </div>
        </a>
        <div className="dl-header-actions">
          <a href="/#areas" className="dl-back-link">← Volver al inicio</a>
          <a href={waUrl} target="_blank" rel="noopener noreferrer" className="dl-btn-wa">
            Consulta gratis
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="dl-hero">
        <div className="dl-hero-inner">
          {variante ? <HeroVariante v={variante} /> : (
            <>
              <div className="dl-hero-label">Área de práctica</div>
              <h1 className="dl-hero-title">Derecho <em>Civil</em></h1>
              <p className="dl-hero-desc">
                Preguntas frecuentes, orientación legal y todo lo que necesitas saber sobre arrendamiento, herencias, cobro de deudas y contratos.
              </p>
            </>
          )}
          <a href={waUrl} target="_blank" rel="noopener noreferrer" className="btn-whatsapp dl-btn-wa-cta">
            <WhatsAppIcon size={17} /> Consulta gratuita
          </a>
          <div className="dl-hero-respuesta">{promesaDeRespuesta()} · Primera consulta gratis</div>
          <GoogleRatingBadge className="rating-badge--hero dl-hero-rating" />
        </div>
      </section>

      {/* FAQ */}
      <section className="dl-faq-section">
        <div className="dl-faq-intro">
          <p>Reunimos las preguntas más frecuentes que nos hacen nuestros clientes en materia civil. Si tu caso no aparece aquí, escríbenos: <strong>la primera orientación es gratuita</strong>.</p>
        </div>

        <div className="dl-faq-list">
          {groups.map((group) => (
            <React.Fragment key={group.label}>
              <div className="dl-faq-group-label">{group.label}</div>
              {group.items.map((faq) => {
                const isOpen = openItems.has(faq.id);
                return (
                  <div
                    key={faq.id}
                    id={`faq-${faq.id}`}
                    className={`dl-faq-item${isOpen ? ' dl-faq-item--open' : ''}`}
                  >
                    <button
                      className="dl-faq-question"
                      onClick={() => toggle(faq.id)}
                      aria-expanded={isOpen}
                    >
                      <span>{faq.question}</span>
                      <span className="dl-faq-icon" aria-hidden="true">
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    <div className="dl-faq-answer">
                      <div className="dl-faq-answer-inner">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="dl-cta-section">
        <div className="dl-cta-inner">
          <div className="dl-cta-label">¿Tienes dudas sobre tu situación?</div>
          <h2 className="dl-cta-title">Revisamos tu caso <em>sin costo</em></h2>
          <p className="dl-cta-desc">
            Contáctanos por WhatsApp. Nuestras abogadas te orientarán de forma clara y directa sobre qué opciones tienes y qué pasos puedes seguir.
          </p>
          <div className="dl-cta-actions">
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className="btn-whatsapp dl-btn-wa-cta">
              <WhatsAppIcon size={17} /> Escribir por WhatsApp
            </a>
            <a href="/#agendar" className="dl-btn-ghost">
              Agendar consulta
            </a>
          </div>
          <p className="dl-cta-note">
            Oficina en Concepción Centro y Lomas Coloradas · +56 9 3924 1385
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="dl-footer">
        <p>© 2026 Souza &amp; Villa Abogadas · <a href="/">svabogadas.cl</a></p>
      </footer>

      <WhatsAppButton />
    </div>
  );
};

export default DerechoCivil;
